import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Icon, { type IconName } from "../AppIcon";
import { useToast, ToastRenderer } from "../../hooks/useToast";
import { useMySubscription } from "../../hooks/useMySubscription";
import { cn } from "../../utils/cn";
import BrochureOverlay from "./BrochureOverlay";
import {
  requestUpgrade,
  notifyHrToUpgrade,
  type SubscriptionStatus,
  type SubscriptionTier,
} from "../../api/General/Subscription";
import { Roles } from "../../models/general";
import { track, AnalyticsEvents } from "../../utils/analytics";

/**
 * Global sticky banner shown to Free / Trial / soon-to-expire-Paid users.
 *
 * Lives in MasterLayout immediately below the TopBar so every routed page
 * inherits it. Operators don't see it; users on healthy long-term Paid plans
 * don't see it. The whole band is a single big "expand" affordance — that's
 * intentional: a free user shouldn't have to hunt for the chevron.
 *
 * Design rationale (impeccable: distill + harden):
 *   - Slim, ~44px, single line on desktop. We don't want to take a giant chunk
 *     of vertical estate from people who paid us by reading a banner about
 *     paying us.
 *   - Tone is set by tier+status, not loudness. The Free band is calm indigo;
 *     a healthy Trial is calm amber; a Trial in its final week is amber-100
 *     with a stronger border to nudge — but not flash.
 *   - Single primary CTA. The "see what's included" affordance is the whole
 *     band, not a competing button. Two CTAs in a banner is a recipe for
 *     ignoring both.
 *   - Operator-preview hook: `?previewTier=free|trial|paid` lets an Operator
 *     dogfood the banner without changing any company state.
 */

export type BannerVariant =
  | "free"
  | "trial-healthy"
  | "trial-ending"
  | "trial-expired"
  | "paid-expiring";

export interface BannerState {
  variant: BannerVariant;
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  daysRemaining: number | null;
  canStartTrial: boolean;
}

const SubscriptionBanner: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { data, isLoading, isError, tier, status, isOperator, isFree, isTrial, isPaid } =
    useMySubscription();
  const role = useSelector((s: { user?: { role?: string } }) => s.user?.role);
  const { toast, showToast, dismissToast } = useToast();

  const [expanded, setExpanded] = useState(false);
  const [requestPending, setRequestPending] = useState(false);
  const isRtl = i18n.dir() === "rtl";

  // -------------------- Operator preview --------------------
  // Operators can dogfood the banner without mutating company state via
  // ?previewTier=free|trial|paid&previewDays=N. The override is per-page-visit
  // and only honoured when the live response says we're an Operator — so
  // a regular user can't smuggle in a different banner state via URL.
  const previewState = useMemo<BannerState | null>(() => {
    const params = new URLSearchParams(location.search);
    const previewTier = params.get("previewTier");
    const previewDaysRaw = params.get("previewDays");
    if (!previewTier) return null;
    if (!isOperator && data && !data.isOperator) return null; // gate on real role

    const previewDays = previewDaysRaw != null ? Number(previewDaysRaw) : null;
    const lower = previewTier.toLowerCase();
    if (lower === "free") {
      return {
        variant: "free",
        tier: "Free",
        status: "Active",
        daysRemaining: null,
        canStartTrial: true,
      };
    }
    if (lower === "trial") {
      const days = previewDays ?? 14;
      return {
        variant: days <= 7 ? "trial-ending" : "trial-healthy",
        tier: "Trial",
        status: "Active",
        daysRemaining: days,
        canStartTrial: false,
      };
    }
    if (lower === "trial-expired") {
      return {
        variant: "trial-expired",
        tier: "Free",
        status: "TrialExpired",
        daysRemaining: null,
        canStartTrial: true,
      };
    }
    if (lower === "paid") {
      const days = previewDays ?? 21;
      return {
        variant: "paid-expiring",
        tier: "Paid",
        status: "Active",
        daysRemaining: days,
        canStartTrial: false,
      };
    }
    return null;
  }, [location.search, isOperator, data]);

  // -------------------- Live state -> variant --------------------
  // Note: the spec keeps the banner hidden on healthy long-term Paid plans
  // (paidDaysRemaining is null OR > 30). We only nudge when renewal is near.
  const liveState = useMemo<BannerState | null>(() => {
    if (!data || isLoading || isError) return null;
    if (data.isOperator) return null;

    if (isFree) {
      if (status === "TrialExpired") {
        return {
          variant: "trial-expired",
          tier,
          status,
          daysRemaining: null,
          canStartTrial: data.canStartTrial,
        };
      }
      return {
        variant: "free",
        tier,
        status,
        daysRemaining: null,
        canStartTrial: data.canStartTrial,
      };
    }
    if (isTrial) {
      const days = data.trialDaysRemaining ?? 0;
      return {
        variant: days <= 7 ? "trial-ending" : "trial-healthy",
        tier,
        status,
        daysRemaining: days,
        canStartTrial: false,
      };
    }
    if (isPaid) {
      const days = data.paidDaysRemaining;
      if (days == null) return null; // indefinite Paid — silence the banner
      if (days >= 30) return null;
      return {
        variant: "paid-expiring",
        tier,
        status,
        daysRemaining: days,
        canStartTrial: false,
      };
    }
    return null;
  }, [data, isLoading, isError, isFree, isTrial, isPaid, status, tier]);

  const state = previewState ?? liveState;

  // -------------------- Body scroll-lock while overlay open --------------------
  useEffect(() => {
    if (!expanded) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [expanded]);

  const handleToggle = useCallback(() => setExpanded((v) => !v), []);
  const handleClose = useCallback(() => setExpanded(false), []);

  const handleStartTrial = useCallback(
    (e?: React.SyntheticEvent) => {
      e?.stopPropagation();
      track(AnalyticsEvents.StartTrialCtaClicked, { source: "banner", variant: state?.variant });
      // Hr/Admin go straight to the upgrade flow. Anyone else (Manager/Chief/
      // Clocking) gets a polite "ask your admin" toast — Phase 7 will surface
      // a proper Notification once that pipeline exists.
      const callerIsHrOrAdmin =
        role === Roles.Hr || role === "Hr" || role === Roles.Admin || role === "Admin";
      if (state?.canStartTrial || callerIsHrOrAdmin) {
        setExpanded(false);
        navigate("/start-trial");
      } else {
        showToast(t("Please ask your HR or admin to start the trial."), "info");
      }
    },
    [navigate, role, showToast, state, t]
  );

  const handleRequestUpgrade = useCallback(
    async (e?: React.SyntheticEvent) => {
      e?.stopPropagation();
      if (requestPending) return;
      setRequestPending(true);
      try {
        const res = await requestUpgrade({ message: null });
        if (res.errors && res.errors.length > 0) {
          showToast(
            res.errors[0]?.description ||
              t("We couldn't send your request — please try again."),
            "error"
          );
        } else {
          track(AnalyticsEvents.UpgradeRequestSubmitted, { source: "banner" });
          showToast(t("Request sent — we'll be in touch."), "success");
        }
      } catch {
        showToast(t("We couldn't send your request — please try again."), "error");
      } finally {
        setRequestPending(false);
      }
    },
    [requestPending, showToast, t]
  );

  const handleNudgeHr = useCallback(
    async (e?: React.SyntheticEvent) => {
      e?.stopPropagation();
      if (requestPending) return;
      setRequestPending(true);
      try {
        const res = await notifyHrToUpgrade();
        if (res.errors && res.errors.length > 0) {
          showToast(
            res.errors[0]?.description ||
              t("Couldn't notify your HR — please contact support."),
            "error"
          );
        } else {
          track(AnalyticsEvents.NudgeHrSubmitted, { source: "banner" });
          showToast(t("Your HR has been notified."), "success");
        }
      } catch {
        showToast(t("Couldn't notify your HR — please contact support."), "error");
      } finally {
        setRequestPending(false);
      }
    },
    [requestPending, showToast, t]
  );

  // -------------------- Render guards --------------------
  if (!state) return null;

  const palette = paletteFor(state.variant);
  const message = messageFor(state, t);

  // Per-variant primary CTA. Closes over the same handlers so the band stays
  // the canonical "expand" target while the button performs the verb.
  let primary: { label: string; onClick: (e: React.MouseEvent) => void } | null = null;
  switch (state.variant) {
    case "free":
      primary = state.canStartTrial
        ? { label: t("Start free trial"), onClick: handleStartTrial }
        : { label: t("Ask your HR to upgrade"), onClick: handleNudgeHr };
      break;
    case "trial-healthy":
      primary = { label: t("See what's included"), onClick: handleToggle };
      break;
    case "trial-ending":
      primary = { label: t("Request upgrade"), onClick: handleRequestUpgrade };
      break;
    case "trial-expired":
      primary = state.canStartTrial
        ? { label: t("Start trial again"), onClick: handleStartTrial }
        : { label: t("Ask your HR to upgrade"), onClick: handleNudgeHr };
      break;
    case "paid-expiring":
      primary = { label: t("Request renewal"), onClick: handleRequestUpgrade };
      break;
  }

  return (
    <>
      {/*
        Sticky band. We deliberately use a button-like div so the whole row is
        a single tap target without breaking the visual rhythm with a <button>
        outline. The aria-expanded + role="button" make it accessible regardless.
      */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-controls="subscription-brochure-overlay"
        aria-label={t("Subscription status — click to expand details")}
        onClick={handleToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleToggle();
          }
        }}
        className={cn(
          // Sticky directly under the 64px TopBar. The z-index sits below the
          // PendingTermsGate (which is z-[100]+) and above page content.
          "sticky top-0 z-30 w-full",
          "border-b transition-colors duration-200 cursor-pointer select-none",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
          palette.band
        )}
      >
        <div className="mx-auto flex h-11 max-w-7xl items-center gap-3 px-4 md:px-6 rtl:flex-row-reverse">
          {/* Status pip — a tiny dot, not a badge, so the band stays slim */}
          <span
            aria-hidden="true"
            className={cn("inline-block h-2 w-2 flex-shrink-0 rounded-full", palette.pip)}
          />

          <Icon
            name={palette.icon}
            size={16}
            className={cn("flex-shrink-0", palette.iconClass)}
            aria-hidden="true"
          />

          {/* Message — single line, truncates on small screens so the CTA never disappears */}
          <p className={cn("flex-1 truncate text-sm font-medium", palette.text)}>
            {message}
          </p>

          {/* Primary CTA — kept as a real button so its keyboard semantics don't
              collide with the band-as-button. stopPropagation prevents the
              outer click from also toggling the overlay. */}
          {primary && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                primary!.onClick(e);
              }}
              onKeyDown={(e) => e.stopPropagation()}
              className={cn(
                "hidden md:inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold",
                "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1",
                palette.cta
              )}
            >
              {primary.label}
              <Icon name="ArrowRight" size={12} className={isRtl ? "rotate-180" : ""} />
            </button>
          )}

          {/* Expand chevron — flips on toggle. aria-label keeps it speakable. */}
          <span
            aria-hidden="true"
            className={cn(
              "flex-shrink-0 transition-transform duration-200",
              expanded ? "rotate-180" : "rotate-0",
              palette.iconClass
            )}
          >
            <Icon name="ChevronDown" size={16} />
          </span>
        </div>
      </div>

      {/*
        Brochure overlay — anchored below the 64px TopBar + this 44px banner.
        Lives in a separate component so the banner stays focused on the slim
        sticky surface and the overlay can own focus-trap / ESC / scroll-lock.
      */}
      <BrochureOverlay
        open={expanded}
        onClose={handleClose}
        state={state}
        onStartTrial={handleStartTrial}
        onRequestUpgrade={handleRequestUpgrade}
      />

      {/* Local toaster — used by the "Request sent" stub and the "ask your admin" fallback. */}
      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Variant -> message / CTA helpers (kept beside the component so the state
// machine is readable end-to-end without jumping files)
// ─────────────────────────────────────────────────────────────────────────────

function messageFor(
  state: BannerState,
  t: (k: string, o?: Record<string, unknown>) => string
): string {
  switch (state.variant) {
    case "free":
      return t("You're on the free version of Mowazaf — explore everything you get.");
    case "trial-healthy":
      return t("{{days}} days left in your trial.", { days: state.daysRemaining ?? 0 });
    case "trial-ending":
      return t("Trial ends in {{days}} days — request continuation.", {
        days: state.daysRemaining ?? 0,
      });
    case "trial-expired":
      return t("Trial ended — upgrade to keep your full workspace.");
    case "paid-expiring":
      return t("Renewal due in {{days}} days.", { days: state.daysRemaining ?? 0 });
    default:
      return "";
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Visual palette per variant
// ─────────────────────────────────────────────────────────────────────────────

interface BannerPalette {
  band: string;
  text: string;
  icon: IconName;
  iconClass: string;
  pip: string;
  cta: string;
}

function paletteFor(variant: BannerVariant): BannerPalette {
  switch (variant) {
    case "free":
      return {
        band: "bg-indigo-50 border-indigo-100 hover:bg-indigo-100/70",
        text: "text-indigo-900",
        icon: "Sparkles",
        iconClass: "text-indigo-600",
        pip: "bg-indigo-500",
        cta:
          "bg-indigo-600 text-white hover:bg-indigo-700 focus-visible:ring-indigo-500",
      };
    case "trial-healthy":
      return {
        band: "bg-amber-50 border-amber-100 hover:bg-amber-100/70",
        text: "text-amber-900",
        icon: "Rocket",
        iconClass: "text-amber-700",
        pip: "bg-amber-500",
        cta:
          "bg-amber-600 text-white hover:bg-amber-700 focus-visible:ring-amber-500",
      };
    case "trial-ending":
      return {
        band: "bg-amber-100 border-amber-300 hover:bg-amber-200/70",
        text: "text-amber-950",
        icon: "Clock",
        iconClass: "text-amber-800",
        pip: "bg-amber-600",
        cta:
          "bg-amber-700 text-white hover:bg-amber-800 focus-visible:ring-amber-600",
      };
    case "trial-expired":
      return {
        band: "bg-rose-50 border-rose-200 hover:bg-rose-100/70",
        text: "text-rose-900",
        icon: "AlertCircle",
        iconClass: "text-rose-600",
        pip: "bg-rose-500",
        cta: "bg-rose-600 text-white hover:bg-rose-700 focus-visible:ring-rose-500",
      };
    case "paid-expiring":
      return {
        band: "bg-slate-50 border-slate-200 hover:bg-slate-100/70",
        text: "text-slate-900",
        icon: "CalendarClock",
        iconClass: "text-slate-600",
        pip: "bg-slate-500",
        cta:
          "bg-slate-900 text-white hover:bg-slate-800 focus-visible:ring-slate-700",
      };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Re-export the primary-CTA factory so JSX above can pick the right action.
// (We don't actually export it — but the inline version in the component uses
//  the same switch. Keeping the canonical source here for future test coverage.)
// ─────────────────────────────────────────────────────────────────────────────

export default SubscriptionBanner;
